import { z } from "zod";

export const HeroClusterStatusSchema = z.enum(["OPEN", "REJECTED", "RESOLVED"]);
export type HeroClusterStatus = z.infer<typeof HeroClusterStatusSchema>;
export const HeroClusterStatus = HeroClusterStatusSchema.Enum;

export const HeroFindingConfidenceSchema = z.enum(["LOW", "MEDIUM", "HIGH"]);
export type HeroFindingConfidence = z.infer<typeof HeroFindingConfidenceSchema>;
export const HeroFindingConfidence = HeroFindingConfidenceSchema.Enum;

/**
 * Config for the scheduled Hero pipeline (analysis followed by
 * aggregation), delivered via the `hero-pipeline-config` LaunchDarkly
 * flag (per-environment).
 */
export const HeroPipelineConfigSchema = z.object({
  /** Master switch for the whole pipeline (both phases). */
  enabled: z.boolean(),
  /**
   * How long after analysis prep completes that the analysis orchestrator
   * may keep dispatching new analyzer runs. In-flight runs are allowed to finish
   * past the deadline (up to the analyzer workflow timeout).
   * Aggregation runs after analysis settles regardless.
   */
  analysisDispatchDeadlineMinutes: z.number(),
  maxConcurrentAnalyzers: z.number(),
  /** How far back from pipeline start to look for eligible threads. */
  eligibleThreadsForAnalysisLookbackHours: z.number(),
  /**
   * Max eligible threads fetched per analysis orchestrator run, i.e.
   * across all analyzer agents dispatched over the course of
   * one analysis orchestrator run.
   */
  eligibleThreadsForAnalysisLimit: z.number(),
  /** Kill switch for the aggregation phase; analysis can run without it. */
  aggregationEnabled: z.boolean(),
  /**
   * Aggregator concurrency defaults to 1 (sequential execution):
   * each aggregator snapshots existing clusters at setup,
   * so concurrent aggregators would mint duplicate clusters.
   */
  maxConcurrentAggregators: z.number(),
  /**
   * How far back to look for unclustered findings to aggregate. Covers the
   * current run's fresh findings plus stragglers from failed or skipped
   * prior runs.
   */
  unclusteredFindingLookbackHours: z.number(),
  /**
   * Max batches per Hero aggregation orchestrator run, i.e.
   * how many aggregator agents can be dispatched in total
   * over the course of one aggregation orchestrator run.
   */
  maxBatchesPerAggregationOrchestratorRun: z.number(),
  /** Max unclustered findings passed to a single Hero aggregator agent run. */
  maxUnclusteredFindingsPerAggregatorAgentRun: z.number(),
  /**
   * Max existing clusters loaded into a single Hero aggregator agent run's
   * context (bounds prompt size vs. clustering recall against prior clusters).
   */
  maxExistingClustersPerAggregatorAgentRun: z.number(),
  /** Min findings required to promote a new Hero cluster. */
  minFindingsPerNewCluster: z.number(),
  /** Min distinct source threads required to promote a new Hero cluster. */
  minDistinctThreadsPerNewCluster: z.number(),
});
export type HeroPipelineConfig = z.infer<typeof HeroPipelineConfigSchema>;

export const defaultHeroPipelineConfig: HeroPipelineConfig = {
  analysisDispatchDeadlineMinutes: 300,
  enabled: false,
  maxConcurrentAnalyzers: 5,
  eligibleThreadsForAnalysisLookbackHours: 24,
  /**
   * Conservative default so accidental enables won't
   * initiate an excessive number of analyzer agents.
   */
  eligibleThreadsForAnalysisLimit: 10,
  aggregationEnabled: false,
  maxConcurrentAggregators: 1,
  unclusteredFindingLookbackHours: 7 * 24,
  maxBatchesPerAggregationOrchestratorRun: 5,
  maxUnclusteredFindingsPerAggregatorAgentRun: 50,
  maxExistingClustersPerAggregatorAgentRun: 50,
  minFindingsPerNewCluster: 3,
  minDistinctThreadsPerNewCluster: 3,
};
